import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useUsersCountByStatuses } from "entities/broadcasts/queries/useUsersCountByStatuses";
import { Target } from "entities/broadcasts/types";
import {
  Status as UserStatus,
  usersStatusesOptions,
} from "entities/users/types";
import { FC } from "react";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import AutocompleteField from "shared/components/Form/AutocompleteField";
import Select from "shared/components/Form/Select";
import TextField from "shared/components/Form/TextField";
import FieldLabel from "shared/components/Form/components/FieldLabel";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import { COLOR_BLACK, COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { CheckBoxIcon } from "shared/theme/icons/CheckBoxIcon";
import { CheckedBoxIcon } from "shared/theme/icons/CheckedBoxIcon";
import { useBroadcastDetails } from "../../context";
import Skeleton from "./skeleton";

const userStatusDisplayMap: Record<UserStatus, string> = {
  [UserStatus.REGISTERED]: "Registered",
  [UserStatus.BOT_INTERACTION_ONLY]: "Bot Interaction Only",
  [UserStatus.DEPOSITED]: "Deposited",
};

const targetDisplayMap: Record<Target, string> = {
  [Target.ALL]: "All users",
  [Target.SPECIFIC]: "Specific users",
};

const getStatusDisplay = (status: UserStatus): string => {
  return userStatusDisplayMap[status] || status;
};

const UserSegmentation: FC = () => {
  const {
    isLoadingBroadcastDetails,
    isReadOnlyMode,
    target,
    setTarget,
    usersStatuses,
    setUsersStatuses,
  } = useBroadcastDetails();

  const isVisibleUsersStatusesField = target === Target.SPECIFIC;

  const { data: userCount, isLoading } = useUsersCountByStatuses({
    usersStatuses,
  });

  return (
    <ContentSection title="User Segmentation">
      {isLoadingBroadcastDetails && <Skeleton />}
      {!isLoadingBroadcastDetails && (
        <FlexColumn rowGap={"1rem"}>
          <Select
            id="target"
            label="Who do you want to target?"
            labelColor={COLOR_BLACK}
            placeholder="Select a target"
            disabledPlaceholder
            fullWidth
            required
            value={target ?? undefined}
            handleChange={(value) => setTarget(value as Target)}
            options={Object.values(Target).map((target) => ({
              value: target,
              label: targetDisplayMap[target],
            }))}
            disabled={isReadOnlyMode}
          />

          {isVisibleUsersStatusesField && (
            <AutocompleteField
              multiple
              disableCloseOnSelect
              id="usersStatuses"
              value={usersStatuses}
              options={usersStatusesOptions}
              onChange={(e, newValue) => {
                setUsersStatuses(newValue);
              }}
              getOptionLabel={(option) => getStatusDisplay(option)}
              isOptionEqualToValue={(option, value) => option === value}
              limitTags={2}
              renderOption={(props, option, { selected }) => {
                return (
                  <li
                    {...props}
                    key={props.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      backgroundColor: selected
                        ? "rgba(0, 0, 0, 0.04)"
                        : "transparent",
                    }}
                  >
                    <Checkbox
                      icon={<CheckBoxIcon />}
                      checkedIcon={<CheckedBoxIcon />}
                      style={{ marginRight: 12 }}
                      checked={selected}
                      id={props.id}
                    />
                    <Typography
                      color={COLOR_BLACK}
                      variant="bodyMRegular"
                      style={{
                        fontWeight: selected ? 500 : 400,
                      }}
                    >
                      {getStatusDisplay(option)}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <>
                  <FieldLabel
                    label="Filter users by status *"
                    labelColor={COLOR_BLACK}
                  />
                  <TextField
                    {...params}
                    placeholder="Add a users statuses..."
                  />
                </>
              )}
            />
          )}
          {isLoading ? (
            <CircularProgress
              size={20}
              sx={{
                color: COLOR_PRIMARY_MAIN,
              }}
            />
          ) : (
            <Typography color={COLOR_PRIMARY_MAIN} variant="bodyMSemiBold">
              *{userCount} users selected
            </Typography>
          )}
        </FlexColumn>
      )}
    </ContentSection>
  );
};

export default UserSegmentation;
